import React from "react";
import { Modal } from "../components/Modal";
import { SecretReveal } from "../components/SecretReveal";
import { Distill } from "../components/Distill";
import { useToast } from "../design/Toast";
import { getKeysUsage, mintToken, revokeToken } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { BOOKING_URL } from "../lib/contact";
import { fmtDate, humanize } from "../lib/format";
import { getHashParam, setHashParam } from "../lib/hashState";
import type { AuthHeaders } from "../lib/auth";
import type { ApiKey, KeyUsage, Me } from "../lib/types";
import {
  Callout,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  IconTile,
  Input,
  LinkButton,
  Menu,
  Page,
  Pill,
  Table,
} from "../design";

const SOON_MS = 14 * 24 * 3600 * 1000;

const KIND_TIP =
  "CLI keys (ck_sub_) are made when you sign in with the dense CLI; their traffic counts against your plan's weekly allowance. API keys (ck_api_) are the ones you create here for scripts and CI; condense's fee for their traffic comes out of your credits.";

function keyKind(k: ApiKey): "CLI" | "API" | null {
  const kind = k.kind ?? (k.prefix?.startsWith("ck_sub_") ? "sub" : null);
  if (kind === "sub") return "CLI";
  if (kind === "api" || k.prefix?.startsWith("ck_api_")) return "API";
  return null;
}

function ExpiryPill({ k }: { k: ApiKey }) {
  if (!k.expires_at) return <Pill>Never</Pill>;
  const left = new Date(k.expires_at).getTime() - Date.now();
  if (left <= 0)
    return (
      <Pill tone="bad" icon="x-circle">
        Expired
      </Pill>
    );
  if (left < SOON_MS)
    return (
      <Pill tone="warn" icon="alert">
        {fmtDate(k.expires_at)}
      </Pill>
    );
  return <Pill>{fmtDate(k.expires_at)}</Pill>;
}

function MintModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: (name: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [name, setName] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <Modal
      title="Create key"
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={() => onConfirm(name.trim())}
          >
            {busy ? "Creating…" : "Create key"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm(name.trim());
        }}
      >
        <Field
          label="Name"
          hint="Optional. This makes an API key (ck_api_) for your own scripts and CI; condense's fee for its traffic comes out of your credits."
        >
          <Input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={64}
            placeholder="Work laptop"
            block
            className="per-block"
          />
        </Field>
      </form>
    </Modal>
  );
}

function AccessModal({
  email,
  onClose,
  onEmail,
}: {
  email: string | null | undefined;
  onClose: () => void;
  onEmail: () => void;
}) {
  const subject = `API access${email ? ` for ${email}` : ""}`;
  return (
    <Modal
      title="Ask for API access"
      onClose={onClose}
      footer={
        <>
          <LinkButton
            variant="ghost"
            href={BOOKING_URL}
            target="_blank"
            rel="noopener"
            iconRight="arrow-up-right"
          >
            Book a call instead
          </LinkButton>
          <LinkButton
            variant="primary"
            icon="mail"
            href={`mailto:team@condense.chat?subject=${encodeURIComponent(subject)}`}
            onClick={onEmail}
          >
            Email us
          </LinkButton>
        </>
      }
    >
      <p className="per-muted">
        We turn API keys on by hand for now, usually within one working day.
        Email us from {email || "your account's address"} and say what you want
        to call condense from; we reply by email once keys are on.
      </p>
    </Modal>
  );
}

export function Keys({
  me,
  authHeaders,
  refresh,
}: {
  me: Me;
  authHeaders: AuthHeaders;
  refresh: () => Promise<void>;
}) {
  const keys = me.keys || [];
  const [flash, setFlash] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(
    () => !!me.api_key_enabled && getHashParam("new") === "1",
  );
  const [askOpen, setAskOpen] = React.useState(false);
  const [minting, setMinting] = React.useState(false);
  React.useEffect(() => {
    if (getHashParam("new")) setHashParam("new", null);
  }, []);
  const toast = useToast();
  const [usage, setUsage] = React.useState<Record<string, KeyUsage>>({});

  React.useEffect(() => {
    let alive = true;
    getKeysUsage(authHeaders, 30).then((u) => {
      if (!alive) return;
      const by: Record<string, KeyUsage> = {};
      for (const k of u.keys) if (k.key_id) by[k.key_id] = k;
      setUsage(by);
    });
    return () => {
      alive = false;
    };
  }, [authHeaders]);

  async function confirmMint(name: string) {
    setMinting(true);
    try {
      const secret = await mintToken(authHeaders, name || null);
      setFlash(secret);
      await copyText(secret);
      setModalOpen(false);
      await refresh();
    } catch {
      toast("Couldn't create the key. Nothing was saved; try again.", {
        tone: "bad",
      });
    } finally {
      setMinting(false);
    }
  }

  async function revoke(id: string) {
    if (
      !window.confirm("Delete this API key? Apps using it will stop working.")
    )
      return;
    try {
      await revokeToken(authHeaders, id);
    } catch {
      toast("Couldn't delete the key. It still works; try again.", {
        tone: "bad",
      });
      return;
    }
    await refresh();
  }

  return (
    <Page
      title="My API keys"
      actions={
        me.api_key_enabled && (
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setModalOpen(true)}
          >
            Create key
          </Button>
        )
      }
    >
      {!me.api_key_enabled && (
        <Card
          title="No API key needed for the CLI"
          icon={<IconTile icon="check" tone="green" />}
          actions={
            <Button size="sm" onClick={() => setAskOpen(true)}>
              Ask for API access
            </Button>
          }
        >
          <p className="per-muted">
            The dense CLI signs you in from the terminal, so your agents already
            work. Keys are for calling condense from your own scripts or CI;
            they come with organization accounts, or ask us to enable them.
          </p>
        </Card>
      )}

      {flash && (
        <Card
          className="per-flash fade-in"
          title={
            <Distill
              phrase="Copy this now — it won't be shown again."
              triggerKey={flash}
            />
          }
          actions={
            <IconButton
              icon="x"
              label="Dismiss"
              onClick={() => setFlash(null)}
            />
          }
        >
          <SecretReveal value={flash} />
        </Card>
      )}

      {(() => {
        const soon = keys.filter(
          (k) =>
            k.expires_at &&
            new Date(k.expires_at).getTime() - Date.now() < SOON_MS,
        );
        if (soon.length === 0) return null;
        return (
          <Callout
            tone="warn"
            icon="clock"
            role="status"
            action={
              me.api_key_enabled && (
                <Button size="sm" onClick={() => setModalOpen(true)}>
                  Create key
                </Button>
              )
            }
          >
            {soon.length === 1
              ? `${soon[0].name || soon[0].prefix || "A key"} expires ${fmtDate(soon[0].expires_at!)}.`
              : `${soon.length} keys expire within two weeks.`}{" "}
            Requests with an expired key are refused; create a new one first.
          </Callout>
        );
      })()}
      <Card
        flush
        title="Active keys"
        info={`${KIND_TIP} Requests and tokens saved cover the last 30 days.`}
      >
        {keys.length === 0 ? (
          <EmptyState icon="keys" title="No keys yet">
            {me.api_key_enabled ? "Create one, or run" : "Run"}{" "}
            <span className="mono">dense</span> in a terminal and sign in when
            it asks; it creates a key for you.
          </EmptyState>
        ) : (
          <Table label="API keys">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th className="num hide-sm">Requests</th>
                <th className="num hide-sm">Tokens saved</th>
                <th className="hide-sm">Created</th>
                <th className="hide-sm">Last used</th>
                <th>Expires</th>
                <th className="actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>
                    <span className={k.name ? "strong" : "muted"}>
                      {k.name || "Unnamed"}
                    </span>
                    {k.prefix && (
                      <span className="ds-cell-sub mono">{k.prefix}</span>
                    )}
                  </td>
                  <td>
                    {keyKind(k) ? (
                      <Pill>{keyKind(k)}</Pill>
                    ) : (
                      <span className="muted">Unknown</span>
                    )}
                  </td>
                  <td className="num hide-sm">
                    {usage[k.id] ? humanize(usage[k.id].requests) : "—"}
                  </td>
                  <td className="num hide-sm">
                    {usage[k.id] ? humanize(usage[k.id].tokens_saved) : "—"}
                  </td>
                  <td className="muted hide-sm">
                    {k.created_at ? fmtDate(k.created_at) : "—"}
                  </td>
                  <td className="muted hide-sm">
                    {k.last_used_at ? fmtDate(k.last_used_at) : "—"}
                  </td>
                  <td>
                    <ExpiryPill k={k} />
                  </td>
                  <td className="actions">
                    <Menu
                      label="Manage API key"
                      items={[
                        {
                          label: "Delete API key",
                          icon: "trash",
                          danger: true,
                          onSelect: () => revoke(k.id),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {askOpen && (
        <AccessModal
          email={me.email}
          onClose={() => setAskOpen(false)}
          onEmail={() => {
            setAskOpen(false);
            toast(
              "Your email app should open with the request. Send it, and we reply within one working day.",
            );
          }}
        />
      )}
      {modalOpen && (
        <MintModal
          busy={minting}
          onConfirm={confirmMint}
          onCancel={() => setModalOpen(false)}
        />
      )}
    </Page>
  );
}
